"use client";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputAdornment,
  Popover,
} from "@material-ui/core";
import { toast } from "react-toastify";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { Calendar, Info } from 'lucide-react';
import {
  editProfileDataAPI,
  getFileByIdAPI,
  getUserPersonalInfo,
} from "../../../../api/userApi";
import BaseLoading from "../../../Loader/BaseLoading";
import defaultAvatars from "../../../../assets/defaultAvatars.svg";
import { useStyles } from "./styles";
import { useAppSelector, useAppDispatch } from "../../../../redux/typedHooks";
import { UserInterface } from "../../../../types/user";
import { COUNTRIES } from "../../../../config/countries";
import UsernameChangeButton from "../../../UsernameChangeButton";
import { validateUsernameChange } from "../../../../utils/usernameValidation";
import { getUserType } from "../../../../utils/getUserType";
import { setUser } from "../../../../redux/modules/userSlice";
import { clsx } from 'clsx';
// @ts-ignore
import MuiPhoneNumber from 'material-ui-phone-number';

interface ProfileData {
  firstName: string;
  lastName: string;
  username: string;
  bio: string;
  email: string;
  DOB: string | null;
  phoneNumber: string;
  gender: string;
  ethnicity: string;
  sexualOrientation: string;
  annualIncome: string;
  website: string;
  location: string;
  country?: string;
  city?: string;
  subdivision?: string;
  profileImg?: File | string | null;
}
const defaultProfile: ProfileData = {
  firstName: "",
  lastName: "",
  username: "",
  bio: "",
  email: "",
  DOB: null,
  phoneNumber: "",
  gender: "",
  ethnicity: "",
  sexualOrientation: "",
  annualIncome: "",
  website: "",
  location: "",
  country: "",
  city: "",
  subdivision: "",
  profileImg: null,
};

// поклади над компонентом або в utils
const formatLastName = (s = "") =>
  s
    .trim()
    .split(/\s+/)
    .map(w =>
      w
        .split("-")
        .map(h =>
          h
            .split(/['’]/)
            .map(p => (p ? p[0].toUpperCase() + p.slice(1).toLowerCase() : p))
            .join("'")
        )
        .join("-")
    )
    .join(" ");

const formatName = (s = "") =>
  s
    .trim()
    .split(/\s+/)
    .map(w =>
      w
        .split("-")
        .map(h =>
          h
            .split(/['’]/)
            .map(p => (p ? p[0].toUpperCase() + p.slice(1).toLowerCase() : p))
            .join("'")
        )
        .join("-")
    )
    .join(" ");

// дозволяємо тільки http/https/mailto, все інше відсікаємо
const toNavigableUrl = (raw?: string | null) => {
  if (!raw) return "";
  let s = String(raw).trim();
  if (!s) return "";

  const scheme = s.match(/^([a-zA-Z][\w+.-]*):/);
  if (scheme) {
    const proto = scheme[1].toLowerCase();
    return proto === "http" || proto === "https" || proto === "mailto" ? s : "";
  }
  if (s.startsWith("//")) return "https:" + s; // підтримка //example.com
  return "https://" + s; // без схеми — додаємо https
};

// опційно, щоб гарніше показувати користувачу без протоколу
const stripProtocol = (s: string) => s.replace(/^https?:\/\//i, "");

const PersonalInformation = () => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [profile, setProfile] = useState<ProfileData>(defaultProfile);
  const [loading, setLoading] = useState(true);
  const [mediaUrl, setMediaUrl] = useState<string | null>(null); // Для BLOB-URL із сервера
  const [previewUrl, setPreviewUrl] = useState<string | null>(null); // Для попереднього перегляду вибраного файлу
  const user = useAppSelector<UserInterface | null>(
    state => state.user.currentUser
  );

  const userType = getUserType(user);
  const isCreator = userType?._id === "6800a63485ec077c7c266c25";
  const isViewer = userType?._id === "6800a68085ec077c7c266c27";
  const [websiteError, setWebsiteError] = useState<string | null>(null);
  const [usernameValidation, setUsernameValidation] = useState<any>(null);
  const [errorDOB, setErrorDOB] = useState("");

  //Popover state for username change info
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement | EventTarget & SVGSVGElement>(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Check username change validation for all users
  useEffect(() => {
    if (user) {
      const normalizedUserType = getUserType(user) || { _id: "", title: "" };
      const validation = validateUsernameChange({
        _id: user.id, // Use 'id' instead of '_id'
        username: user.username,
        usernameUpdatedAt: user.usernameUpdatedAt,
        contentProviderAgreementAccepted: user.contentProviderAgreementAccepted,
        userType: normalizedUserType, // Use normalized userType
      });
      setUsernameValidation(validation);
    }
  }, [user]);

  // Handle username change
  const handleUsernameChanged = async (newUsername: string) => {
    setProfile(prev => ({ ...prev, username: newUsername }));
    // Refresh user data to get updated usernameUpdatedAt
    try {
      const res = await getUserPersonalInfo();
      const { city, country } = parseLocationString(res.data.location);

      setProfile(prev => ({
        ...prev,
        ...res.data,
        city: res.data.city ?? city,
        country: res.data.country ?? country,
        username: newUsername, // Keep the new username
      }));

      // Update Redux store with fresh user data including new fields
      dispatch(
        setUser({
          ...user,
          ...res.data,
          username: newUsername,
        })
      );
    } catch (error) {
      console.error("Error refreshing user data:", error);
    }
  };

  // Функція для завантаження BLOB-URL
  const fetchMediaUrl = async (mediaUrl: string) => {
    if (!mediaUrl) return defaultAvatars;

    // Якщо це Google URL (lh3.googleusercontent.com), використовуємо його напряму
    if (mediaUrl.includes("lh3.googleusercontent.com")) {
      setMediaUrl(mediaUrl);
      return mediaUrl;
    }

    // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
    if (mediaUrl.startsWith("https://") || mediaUrl.startsWith("http://")) {
      setMediaUrl(mediaUrl);
      return mediaUrl;
    }

    const fileId = mediaUrl.split("/api/files/")[1];
    if (!fileId) return defaultAvatars;

    try {
      const blobUrl = await getFileByIdAPI(fileId);
      setMediaUrl(blobUrl);
      return blobUrl;
    } catch (err) {
      console.error(`Error fetching avatar for ${mediaUrl}:`, err);
      toast.error("Failed to load profile avatar");
      return defaultAvatars;
    }
  };

  useEffect(() => {
    getUserFields()
  }, []);

  useEffect(() => {
    return () => {
      if (mediaUrl) URL.revokeObjectURL(mediaUrl);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [mediaUrl, previewUrl]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>
  ) => {
    const target = e.target as HTMLInputElement & {
      name: string;
      value: string;
      checked: boolean;
      type: string;
    };
    const { name, value, type, checked } = target;
    if (!name) return;

    setProfile(prev => {
      const next = { ...prev, [name]: type === "checkbox" ? checked : value };

      if (name === "city" || name === "country") {
        const city = name === "city" ? value : prev.city || "";
        const country = name === "country" ? value : prev.country || "";
        next.location = buildLocationString(city, country);
      }
      return next;
    });
  }

  const inputFileRef = useRef<HTMLInputElement | null>(null);

  const handleFileClick = () => {
    inputFileRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Очищаємо попередній preview URL якщо він існує
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      // Створюємо новий preview URL для вибраного файлу
      const newPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(newPreviewUrl);

      setProfile(prev => ({
        ...prev,
        profileImg: file,
      }));
    }
  };

  function isAdult(dateStr: string): boolean {
    if (!dateStr) return false;
    const birthDate = new Date(dateStr);
    if (isNaN(birthDate.getTime())) return false;

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 18;
  }

  const handleDateChange = (date: Date | null) => {
    if (!date || isNaN(date.getTime())) {
      setErrorDOB("Invalid date format");
      return;
    }
    const dateStr = date ? date.toISOString().split("T")[0] : "";

    if (dateStr && !isAdult(dateStr)) {
      setErrorDOB("You must be at least 18 years old");
    } else {
      setErrorDOB("");
    }
    setProfile((prev => ({
      ...prev,
      DOB: dateStr
    })));
  }

  const getUserFields = async () => {
    setLoading(true);
    try {
      const res = await getUserPersonalInfo();

      // розібрати location -> city/country
      const { city, country } = parseLocationString(res.data.location);

      setProfile({
        ...defaultProfile,
        ...res.data,
        // якщо бек колись почне віддавати окремі поля — беремо їх, інакше беремо з парсера
        city: res.data.city ?? city,
        country: res.data.country ?? country,
      });

      if (res.data.profileImg) {
        await fetchMediaUrl(res.data.profileImg);
      }
    } catch (e) {
      console.error("Failed to load personal info:", e);
      toast.error("Failed to load personal info");
    } finally {
      setLoading(false);
    }
  }

  const handleSave = async () => {
    try {
      // Check if user is trying to change username and validate cooldown
      if (profile.username !== user?.username) {
        if (!usernameValidation?.canChange) {
          toast.error(
            usernameValidation?.reason || "Cannot change username at this time"
          );
          return;
        }
      }

      const formData = new FormData();
      const fixedFirst = formatName(profile.firstName);
      formData.append("firstName", fixedFirst);
      // formData.append("firstName", profile.firstName);
      const fixedLast = formatLastName(profile.lastName);
      formData.append("lastName", fixedLast);
      // formData.append("lastName", profile.lastName);
      formData.append("username", profile.username);
      formData.append("bio", profile.bio);
      formData.append("email", profile.email);
      if (profile.DOB) {
        if (errorDOB.length > 0) {
          return toast.error("Invalid birth date. Please correct it before saving.");
        }
        formData.append("DOB", profile.DOB);
      }
      formData.append("phoneNumber", profile.phoneNumber);
      formData.append("gender", profile.gender);
      formData.append(
        "location",
        buildLocationString(profile.city, profile.country)
      );
      if (profile.subdivision) {
        formData.append("subdivision", profile.subdivision);
      }
      formData.append("ethnicity", profile.ethnicity);
      formData.append("sexualOrientation", profile.sexualOrientation);
      formData.append("website", profile.website);
      formData.append("annualIncome", profile.annualIncome);
      if (profile.profileImg instanceof File) {
        formData.append("image", profile.profileImg);
      }
      const response = await editProfileDataAPI(formData);

      // Update Redux store with fresh user data
      if (response?.data) {
        dispatch(
          setUser({
            ...user,
            ...response.data,
          })
        );

        // Очищаємо preview URL після успішного збереження
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
          setPreviewUrl(null);
        }

        // Оновлюємо mediaUrl з новим зображенням профілю
        if (response.data.profileImg) {
          await fetchMediaUrl(response.data.profileImg);
        }

        // Оновлюємо profile.profileImg на URL замість File
        setProfile(prev => ({
          ...prev,
          profileImg: response.data.profileImg || prev.profileImg,
        }));
      }

      toast.success("Profile updated successfully");
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Failed to update profile");
    }
  };

  const buildLocationString = (city?: string, country?: string) =>
    city && country ? `${city}, ${country}` : city || country || "";

  const parseLocationString = (loc?: string) => {
    if (!loc) return { city: "", country: "" };
    const [cityPart, ...rest] = loc.split(",").map(s => s.trim());
    return { city: cityPart || "", country: rest.join(", ").trim() || "" };
  };
  // Helpers for country flags
  const toFlagEmoji = (code?: string) => {
    if (!code) return "";
    const cc = code.toUpperCase();
    if (cc.length !== 2) return "";
    const A = 0x1f1e6; // Regional Indicator Symbol Letter A
    return String.fromCodePoint(
      A + (cc.charCodeAt(0) - 65),
      A + (cc.charCodeAt(1) - 65)
    );
  };
  const nameToIso: Record<string, string> = {
    Afghanistan: "AF",
    Albania: "AL",
    Algeria: "DZ",
    Andorra: "AD",
    Angola: "AO",
    "Antigua and Barbuda": "AG",
    Argentina: "AR",
    Armenia: "AM",
    Australia: "AU",
    Austria: "AT",
    Azerbaijan: "AZ",
    Bahamas: "BS",
    Bahrain: "BH",
    Bangladesh: "BD",
    Barbados: "BB",
    Belarus: "BY",
    Belgium: "BE",
    Belize: "BZ",
    Benin: "BJ",
    Bhutan: "BT",
    Bolivia: "BO",
    "Bosnia and Herzegovina": "BA",
    Botswana: "BW",
    Brazil: "BR",
    Brunei: "BN",
    Bulgaria: "BG",
    "Burkina Faso": "BF",
    Burundi: "BI",
    "Cabo Verde": "CV",
    Cambodia: "KH",
    Cameroon: "CM",
    Canada: "CA",
    "Central African Republic": "CF",
    Chad: "TD",
    Chile: "CL",
    China: "CN",
    Colombia: "CO",
    Comoros: "KM",
    "Congo (Congo-Brazzaville)": "CG",
    "Costa Rica": "CR",
    Croatia: "HR",
    Cuba: "CU",
    Cyprus: "CY",
    "Czechia (Czech Republic)": "CZ",
    "Democratic Republic of the Congo": "CD",
    Denmark: "DK",
    Djibouti: "DJ",
    Dominica: "DM",
    "Dominican Republic": "DO",
    Ecuador: "EC",
    Egypt: "EG",
    "El Salvador": "SV",
    "Equatorial Guinea": "GQ",
    Eritrea: "ER",
    Estonia: "EE",
    Eswatini: "SZ",
    Ethiopia: "ET",
    Fiji: "FJ",
    Finland: "FI",
    France: "FR",
    Gabon: "GA",
    Gambia: "GM",
    Georgia: "GE",
    Germany: "DE",
    Ghana: "GH",
    Greece: "GR",
    Grenada: "GD",
    Guatemala: "GT",
    Guinea: "GN",
    "Guinea-Bissau": "GW",
    Guyana: "GY",
    Haiti: "HT",
    "Holy See": "VA",
    Honduras: "HN",
    Hungary: "HU",
    Iceland: "IS",
    India: "IN",
    Indonesia: "ID",
    Iran: "IR",
    Iraq: "IQ",
    Ireland: "IE",
    Israel: "IL",
    Italy: "IT",
    Jamaica: "JM",
    Japan: "JP",
    Jordan: "JO",
    Kazakhstan: "KZ",
    Kenya: "KE",
    Kiribati: "KI",
    Kuwait: "KW",
    Kyrgyzstan: "KG",
    Laos: "LA",
    Latvia: "LV",
    Lebanon: "LB",
    Lesotho: "LS",
    Liberia: "LR",
    Libya: "LY",
    Liechtenstein: "LI",
    Lithuania: "LT",
    Luxembourg: "LU",
    Madagascar: "MG",
    Malawi: "MW",
    Malaysia: "MY",
    Maldives: "MV",
    Mali: "ML",
    Malta: "MT",
    "Marshall Islands": "MH",
    Mauritania: "MR",
    Mauritius: "MU",
    Mexico: "MX",
    Micronesia: "FM",
    Moldova: "MD",
    Monaco: "MC",
    Mongolia: "MN",
    Montenegro: "ME",
    Morocco: "MA",
    Mozambique: "MZ",
    "Myanmar (Burma)": "MM",
    Namibia: "NA",
    Nauru: "NR",
    Nepal: "NP",
    Netherlands: "NL",
    "New Zealand": "NZ",
    Nicaragua: "NI",
    Niger: "NE",
    Nigeria: "NG",
    "North Korea": "KP",
    "North Macedonia": "MK",
    Norway: "NO",
    Oman: "OM",
    Pakistan: "PK",
    Palau: "PW",
    "Palestine State": "PS",
    Panama: "PA",
    "Papua New Guinea": "PG",
    Paraguay: "PY",
    Peru: "PE",
    Philippines: "PH",
    Poland: "PL",
    Portugal: "PT",
    Qatar: "QA",
    Romania: "RO",
    Russia: "RU",
    Rwanda: "RW",
    "Saint Kitts and Nevis": "KN",
    "Saint Lucia": "LC",
    "Saint Vincent and the Grenadines": "VC",
    Samoa: "WS",
    "San Marino": "SM",
    "Sao Tome and Principe": "ST",
    "Saudi Arabia": "SA",
    Senegal: "SN",
    Serbia: "RS",
    Seychelles: "SC",
    "Sierra Leone": "SL",
    Singapore: "SG",
    Slovakia: "SK",
    Slovenia: "SI",
    "Solomon Islands": "SB",
    Somalia: "SO",
    "South Africa": "ZA",
    "South Korea": "KR",
    "South Sudan": "SS",
    Spain: "ES",
    "Sri Lanka": "LK",
    Sudan: "SD",
    Suriname: "SR",
    Sweden: "SE",
    Switzerland: "CH",
    Syria: "SY",
    Tajikistan: "TJ",
    Tanzania: "TZ",
    Thailand: "TH",
    "Timor-Leste": "TL",
    Togo: "TG",
    Tonga: "TO",
    "Trinidad and Tobago": "TT",
    Tunisia: "TN",
    Turkey: "TR",
    Turkmenistan: "TM",
    Tuvalu: "TV",
    Uganda: "UG",
    Ukraine: "UA",
    "United Arab Emirates": "AE",
    "United Kingdom": "GB",
    "United States": "US",
    Uruguay: "UY",
    Uzbekistan: "UZ",
    Vanuatu: "VU",
    Venezuela: "VE",
    Vietnam: "VN",
    Yemen: "YE",
    Zambia: "ZM",
    Zimbabwe: "ZW",
  };
  const flagFor = (name: string) => toFlagEmoji(nameToIso[name]);
  const history = useHistory();

  if (loading) return <BaseLoading />;
  if (!profile) return <Typography>Profile not found</Typography>;

  return (
    <Box className={classes.container}>
      <>
        <Box className={classes.contentWrapper}>
          <Typography className={clsx(classes.sectionTitle, classes.sectionMainTitle)}>
            Personal info
          </Typography>
          {/* Profile Photo Section */}
          {/* <Box className={classes.profileSection}>
            <div
              onClick={() => history.push(`/${profile.username}`)}
              style={{ cursor: "pointer" }}
            >
              <Avatar
                src={
                  previewUrl || // Пріоритет: попередній перегляд вибраного файлу
                  (profile.profileImg &&
                    typeof profile.profileImg === "string" &&
                    mediaUrl) ||
                  defaultAvatars
                }
                className={classes.profileImage}
                onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                  console.error("Avatar load error:", profile.profileImg);
                }}
              />
            </div>
            <Box className={classes.profileTextContainer}>
              <Typography
                className={classes.creatorText}
                onClick={() => history.push(`/${profile.username}`)}
                style={{ cursor: "pointer" }}
              >
                {profile.firstName} {profile.lastName}
              </Typography>
              <Typography
                className={classes.creatorTextHelp}
                onClick={() => history.push(`/${profile.username}`)}
                style={{ cursor: "pointer" }}
              >
                @{profile.username}
              </Typography>

              <Button
                className={classes.changePhotoText}
                disableRipple
                onClick={handleFileClick}
              >
                Change profile photo
              </Button>
              <input
                ref={inputFileRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
            </Box>
          </Box> */}

          <Box className={classes.sectionContainer}>
            <Box className={classes.sectionTitleWrapper}>
              {/* Name */}
              <Typography className={classes.sectionTitle}>
                Name
              </Typography>
              <Typography className={classes.sectionDescription}>
                Help people discover your account by using the name you're known by
              </Typography>
            </Box>
            <Box className={classes.formRowNamme}>
              <Box className={classes.formRowNammeContainer}>
                <Box className={classes.inputsBox}>
                  <Typography className={classes.label}>First Name</Typography>
                  <Box className={classes.fieldContainerHelp}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      name="firstName"
                      value={profile.firstName}
                      onChange={handleInputChange}
                      className={classes.inputFieldHelp}
                      onBlur={e =>
                        setProfile(p => ({
                          ...p,
                          firstName: formatName(e.target.value),
                        }))
                      }
                    />
                  </Box>
                </Box>
                <Box className={classes.inputsBox}>
                  <Typography className={classes.label}>Last Name</Typography>
                  <Box className={classes.fieldContainerHelp}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      size="small"
                      name="lastName"
                      value={profile.lastName}
                      onChange={handleInputChange}
                      className={classes.inputFieldHelp}
                      onBlur={e =>
                        setProfile(p => ({
                          ...p,
                          lastName: formatLastName(e.target.value),
                        }))
                      }
                    />
                  </Box>
                </Box>
              </Box>
              {/* <Typography className={classes.helperTextHelp}>
              Help people discover your account by using the name you're known by:
              either your full name, nickname, or business name.
            </Typography>
            <Typography className={classes.helperTextHelp}>
              You can only change your name twice within 14 days.
            </Typography> */}
            </Box>

            {/* Username */}
            <Box className={classes.formRow}>
              <Box className={classes.inputsBox}>
                <Typography className={classes.label}>Username</Typography>
                <Box className={classes.fieldContainer}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="username"
                    value={profile.username}
                    onChange={handleInputChange}
                    className={classes.inputField}
                    disabled={isCreator} // Disable direct editing for creators
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><span style={{ color: "#FFD0E1" }}>@</span></InputAdornment>,
                    }}
                  />
                  <Box className={classes.label} style={{ margin: "8px 0 0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>You can only change your name twice within 14 days.</div>
                    <Info size={15}
                      style={{ color: " #FFD0E1", marginTop: 2, cursor: "pointer" }}
                      onClick={(event) => setAnchorEl(event.currentTarget)}
                      onMouseEnter={(event) => setAnchorEl(event.currentTarget)}
                    />

                    <Popover
                      open={open}
                      anchorEl={anchorEl}
                      onClose={handleClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                      style={{ marginTop: 12 }}
                    >
                      <Box style={{ maxWidth: 386, padding: 12, color: "#FFFFFFCC", fontSize: 14 }}>
                        <div>
                          Changing your username will update all your profile links. Any existing links will automatically redirect to your new username.
                        </div>
                        <Box display="flex" justifyContent="flex-end">
                          <UsernameChangeButton
                            user={{
                              _id: user?.id || "", // Use 'id' instead of '_id'
                              username: user?.username || "",
                              usernameUpdatedAt: user?.usernameUpdatedAt,
                              contentProviderAgreementAccepted:
                                user?.contentProviderAgreementAccepted,
                              userType: getUserType(user) || { _id: "", title: "" },
                            }}
                            onUsernameChanged={handleUsernameChanged}
                            variant="text"
                          />
                          {usernameValidation && !usernameValidation.canChange && (
                            <Typography
                              className={classes.helperText}
                              style={{ color: "#ff9800" }}
                            >
                              {usernameValidation.reason}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </Popover>
                  </Box>
                  {/* <Typography
                  className={classes.creatorTextHelp}
                  style={{ marginTop: 6 }}
                >
                  <a
                    href={`/${profile.username}`}
                    style={{ color: "#FE5A97", textDecoration: "none" }}
                  >
                    {window.location.origin}/{profile.username}
                  </a>
                </Typography> */}
                </Box>
              </Box>
              <Box className={classes.inputsBox}>
                <Typography className={classes.label}>Birth date</Typography>
                <Box className={classes.fieldContainerHelp}>
                  <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <KeyboardDatePicker
                      margin="none"
                      id="date-picker-dialog"
                      format="dd/MM/yy"
                      className={classes.inputFieldHelp}
                      name="DOB"
                      value={profile.DOB ? new Date(profile.DOB) : null}
                      onChange={handleDateChange}
                      KeyboardButtonProps={{
                        "aria-label": "change date",
                      }}
                      inputVariant="outlined"
                      variant="inline"
                      fullWidth
                      size="small"
                      error={Boolean(errorDOB)}
                      helperText={errorDOB}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Calendar style={{ color: "#fff" }} size={20} />
                          </InputAdornment>
                        ),
                      }}
                      keyboardIcon={<KeyboardArrowDownIcon style={{ color: "#fff", fontSize: 20 }} />}
                    />
                  </MuiPickersUtilsProvider>
                </Box>
              </Box>
            </Box>

            {!isViewer && (
              <>
                {/* Bio */}
                <Box className={classes.formRow} style={{ marginBottom: 0 }}>
                  <Box sx={{ width: "100%" }}>
                    <Typography className={classes.label}>Bio</Typography>
                    <Box className={classes.formRowNammeContainer}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        size="small"
                        name="bio"
                        placeholder="Tell other users more about yourself..."
                        value={profile.bio}
                        onChange={handleInputChange}
                        multiline
                        className={classes.inputField}
                        inputProps={{ maxLength: 150, fontSize: 16, fontWeight: 400 }}
                      />
                      {/* <Typography className={classes.bioCounter}>
                      {profile.bio.length} / 150
                    </Typography> */}
                    </Box>
                  </Box>
                </Box>
              </>
            )}
          </Box>

          <Box className={classes.sectionContainer}>
            <Box className={classes.sectionTitleWrapper}>
              {/* Personal Information Section */}
              <Typography className={classes.sectionTitle}>
                Contact info
              </Typography>
            </Box>
            {/* Email */}
            <Box className={classes.formRowNammeContainer}>
              <Box className={classes.inputsBox}>
                <Typography className={classes.label}>Email</Typography>
                <Box className={classes.fieldContainer}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="email"
                    value={profile.email}
                    onChange={handleInputChange}
                    className={classes.inputField}
                  />
                </Box>
              </Box>

              {/* Website */}
              <Box className={classes.inputsBox}>
                <Typography className={classes.label}>Website</Typography>
                <Box className={classes.fieldContainer}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="website"
                    value={stripProtocol(profile.website || "")} // показуємо без http(s)://
                    onChange={handleInputChange}
                    onBlur={e => {
                      const normalized = toNavigableUrl(e.target.value);
                      if (!normalized) {
                        setWebsiteError("Enter a valid URL (http/https).");
                      } else {
                        setWebsiteError(null);
                        setProfile(p => ({ ...p, website: normalized }));
                      }
                    }}
                    error={!!websiteError}
                    helperText={websiteError || ""}
                    placeholder="example.com"
                    className={classes.inputField}
                  />
                  {/* <Typography className={classes.helperText}>
                    example.com or https://example.com
                  </Typography> */}
                </Box>
              </Box>
            </Box>

            <Box className={classes.formRowNammeContainer}>
              <Box className={classes.inputsBox}>
                {/* Phone Number */}
                <Typography className={classes.label}>Phone </Typography>
                <Box className={classes.fieldContainer}>
                  <MuiPhoneNumber
                    defaultCountry="us"
                    variant="outlined"
                    size="small"
                    className={classes.inputField}
                    name="phoneNumber"
                    value={profile.phoneNumber}
                    onChange={(phone: any) => handleInputChange({ target: { name: "phoneNumber", value: phone } } as any)}
                    fullWidth
                  />
                </Box>
              </Box>
            </Box>

          </Box>

          <Box className={classes.sectionContainer}>
            <Box className={classes.sectionTitleWrapper}>
              {/* Location Section */}
              <Typography className={classes.sectionTitle}>
                Location
              </Typography>
            </Box>
            <Box className={classes.formRowNammeContainer}>
              <Box className={classes.inputsBox}>
                {/* Country */}
                <Typography className={classes.label}>Country</Typography>
                <Box className={classes.fieldContainerHelp}>
                  <FormControl
                    variant="outlined"
                    fullWidth
                    size="small"
                    className={classes.inputFieldHelp}
                  >
                    <Select
                      name="country"
                      value={profile.country || ""}
                      onChange={handleInputChange}
                      displayEmpty
                      IconComponent={(props) => (
                        <KeyboardArrowDownIcon {...props} style={{ color: "#fff", fontSize: 20 }} />
                      )}
                      MenuProps={{
                        getContentAnchorEl: null,
                        anchorOrigin: { vertical: "bottom", horizontal: "left" },
                        transformOrigin: { vertical: "top", horizontal: "left" },
                        classes: { paper: classes.dropdownPaper },
                      }}
                    >
                      {["United States", "United Kingdom", "Canada"].map(c => (
                        <MenuItem key={c} className={classes.menuItem} value={c}>
                          <span style={{ marginRight: 8 }}>{flagFor(c)}</span>
                          {c}
                        </MenuItem>
                      ))}
                      <MenuItem
                        disabled
                        className={classes.menuItem}
                        value="__sep__"
                      >
                        ———————————————
                      </MenuItem>
                      {COUNTRIES.filter(
                        c =>
                          !["United States", "United Kingdom", "Canada"].includes(c)
                      ).map(c => (
                        <MenuItem key={c} className={classes.menuItem} value={c}>
                          <span style={{ marginRight: 8 }}>{flagFor(c)}</span>
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Subdivision (State/Province) - shown when available */}
              {(() => {
                const iso = nameToIso[profile.country || ""];
                const SUBDIVISIONS: Record<string, string[]> = {
                  US: [
                    "Alabama",
                    "Alaska",
                    "Arizona",
                    "Arkansas",
                    "California",
                    "Colorado",
                    "Connecticut",
                    "Delaware",
                    "Florida",
                    "Georgia",
                    "Hawaii",
                    "Idaho",
                    "Illinois",
                    "Indiana",
                    "Iowa",
                    "Kansas",
                    "Kentucky",
                    "Louisiana",
                    "Maine",
                    "Maryland",
                    "Massachusetts",
                    "Michigan",
                    "Minnesota",
                    "Mississippi",
                    "Missouri",
                    "Montana",
                    "Nebraska",
                    "Nevada",
                    "New Hampshire",
                    "New Jersey",
                    "New Mexico",
                    "New York",
                    "North Carolina",
                    "North Dakota",
                    "Ohio",
                    "Oklahoma",
                    "Oregon",
                    "Pennsylvania",
                    "Rhode Island",
                    "South Carolina",
                    "South Dakota",
                    "Tennessee",
                    "Texas",
                    "Utah",
                    "Vermont",
                    "Virginia",
                    "Washington",
                    "West Virginia",
                    "Wisconsin",
                    "Wyoming",
                  ],
                  CA: [
                    "Alberta",
                    "British Columbia",
                    "Manitoba",
                    "New Brunswick",
                    "Newfoundland and Labrador",
                    "Nova Scotia",
                    "Ontario",
                    "Prince Edward Island",
                    "Quebec",
                    "Saskatchewan",
                    "Northwest Territories",
                    "Nunavut",
                    "Yukon",
                  ],
                };
                const list = SUBDIVISIONS[iso as keyof typeof SUBDIVISIONS];
                if (!list) return null;
                return (
                  <>
                    <Box className={classes.inputsBox}>
                      <Typography className={classes.labell}>
                        {iso === "US"
                          ? "State"
                          : iso === "CA"
                            ? "Province"
                            : "Region"}
                      </Typography>
                      <Box className={classes.fieldContainerHelp}>
                        <FormControl
                          variant="outlined"
                          fullWidth
                          size="small"
                          className={classes.inputFieldHelp}
                        >
                          <Select
                            name="subdivision"
                            value={profile.subdivision || ""}
                            onChange={handleInputChange}
                            IconComponent={(props) => (
                              <KeyboardArrowDownIcon {...props} style={{ color: "#fff", fontSize: 20 }} />
                            )}
                            MenuProps={{
                              getContentAnchorEl: null,
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "left",
                              },
                              transformOrigin: {
                                vertical: "top",
                                horizontal: "left",
                              },
                              classes: { paper: classes.dropdownPaper },
                            }}
                          >
                            {list.map(s => (
                              <MenuItem
                                key={s}
                                className={classes.menuItem}
                                value={s}
                              >
                                {s}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  </>
                );
              })()}

            </Box>

            <Box sx={{
              width: {
                xs: "100%",
                sm: "50%",
              },
            }}>
              {/* City */}
              <Typography className={classes.labell}>City</Typography>
              <Box className={classes.formRowNammeContainer}>
                <Box className={classes.fieldContainerHelp}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="city"
                    value={profile.city || ""}
                    onChange={handleInputChange}
                    className={classes.inputFieldHelp}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box className={classes.sectionContainer}>
            <Box className={classes.sectionTitleWrapper}>
              {/* About Section */}
              <Typography className={classes.sectionTitle}>
                About
              </Typography>
            </Box>
            <Box className={classes.formRow}>
              {/* Ethnicity */}
              <Box className={classes.inputsBox}>
                <Typography className={classes.label}>Ethnicity</Typography>
                <Box className={classes.fieldContainer}>
                  <FormControl
                    variant="outlined"
                    fullWidth
                    size="small"
                    className={classes.inputField}
                  >
                    <Select
                      name="ethnicity"
                      value={profile.ethnicity}
                      onChange={handleInputChange}
                      MenuProps={{
                        getContentAnchorEl: null,
                        anchorOrigin: { vertical: "bottom", horizontal: "left" },
                        transformOrigin: { vertical: "top", horizontal: "left" },
                        classes: { paper: classes.dropdownPaper },
                      }}
                      IconComponent={(props) => (
                        <KeyboardArrowDownIcon {...props} style={{ color: "#fff", fontSize: 20 }} />
                      )}
                    >
                      <MenuItem className={classes.menuItem} value="Asian">
                        Asian
                      </MenuItem>
                      <MenuItem
                        className={classes.menuItem}
                        value="Black / African Descent"
                      >
                        Black / African Descent
                      </MenuItem>
                      <MenuItem
                        className={classes.menuItem}
                        value="Hispanic / Latino"
                      >
                        Hispanic / Latino
                      </MenuItem>
                      <MenuItem
                        className={classes.menuItem}
                        value="Middle Eastern / North African"
                      >
                        Middle Eastern / North African
                      </MenuItem>
                      <MenuItem
                        className={classes.menuItem}
                        value="Native American / Indigenous"
                      >
                        Native American / Indigenous
                      </MenuItem>
                      <MenuItem className={classes.menuItem} value="Pacific Islander">
                        Pacific Islander
                      </MenuItem>
                      <MenuItem
                        className={classes.menuItem}
                        value="White / Caucasian"
                      >
                        White / Caucasian
                      </MenuItem>
                      <MenuItem
                        className={classes.menuItem}
                        value="Mixed / Multiracial"
                      >
                        Mixed / Multiracial
                      </MenuItem>
                      <MenuItem className={classes.menuItem} value="Other">
                        Other
                      </MenuItem>
                      <MenuItem
                        className={classes.menuItem}
                        value="Prefer not to say"
                      >
                        Prefer not to say
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
              <Box className={classes.inputsBox}>
                {/* sexualOrientation */}
                <Typography className={classes.label}>Sexual Orientation</Typography>
                <Box className={classes.fieldContainer}>
                  <FormControl
                    variant="outlined"
                    fullWidth
                    size="small"
                    className={classes.inputField}
                  >
                    <Select
                      name="sexualOrientation"
                      value={profile.sexualOrientation}
                      onChange={handleInputChange}
                      MenuProps={{
                        getContentAnchorEl: null,
                        anchorOrigin: { vertical: "bottom", horizontal: "left" },
                        transformOrigin: { vertical: "top", horizontal: "left" },
                        classes: { paper: classes.dropdownPaper },
                      }}
                      IconComponent={(props) => (
                        <KeyboardArrowDownIcon {...props} style={{ color: "#fff", fontSize: 20 }} />
                      )}
                    >
                      <MenuItem
                        className={classes.menuItem}
                        value="Straight / Heterosexual"
                      >
                        Straight / Heterosexual
                      </MenuItem>
                      <MenuItem className={classes.menuItem} value="Gay">
                        Gay
                      </MenuItem>
                      <MenuItem className={classes.menuItem} value="Bisexual">
                        Bisexual
                      </MenuItem>
                      <MenuItem className={classes.menuItem} value="Pansexual">
                        Pansexual
                      </MenuItem>
                      <MenuItem className={classes.menuItem} value="Queer">
                        Queer
                      </MenuItem>
                      <MenuItem className={classes.menuItem} value="Asexual">
                        Asexual
                      </MenuItem>
                      <MenuItem className={classes.menuItem} value="Demisexual">
                        Demisexual
                      </MenuItem>
                      <MenuItem className={classes.menuItem} value="Questioning">
                        Questioning
                      </MenuItem>
                      <MenuItem
                        className={classes.menuItem}
                        value="Prefer not to say"
                      >
                        Prefer not to say
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>

            {/* Gender */}
            <Box className={classes.formRow}>
              <Box className={classes.inputsBox}>
                <Typography className={classes.label}>Gender</Typography>
                <Box className={classes.fieldContainer}>
                  <FormControl
                    variant="outlined"
                    fullWidth
                    size="small"
                    className={classes.inputField}
                  >
                    <Select
                      name="gender"
                      value={profile.gender}
                      onChange={handleInputChange}
                      MenuProps={{
                        getContentAnchorEl: null,
                        anchorOrigin: { vertical: "bottom", horizontal: "left" },
                        transformOrigin: { vertical: "top", horizontal: "left" },
                        classes: { paper: classes.dropdownPaper },
                      }}
                      IconComponent={(props) => (
                        <KeyboardArrowDownIcon {...props} style={{ color: "#fff", fontSize: 20 }} />
                      )}                    >
                      <MenuItem className={classes.menuItem} value="Female">
                        Female
                      </MenuItem>
                      <MenuItem className={classes.menuItem} value="Male">
                        Male
                      </MenuItem>
                      <MenuItem
                        className={classes.menuItem}
                        value="Transgender Female (MTF)"
                      >
                        Transgender Female (MTF)
                      </MenuItem>
                      <MenuItem
                        className={classes.menuItem}
                        value="Transgender Male (FTM)"
                      >
                        Transgender Male (FTM)
                      </MenuItem>
                      <MenuItem className={classes.menuItem} value="Non-Binary">
                        Non-Binary
                      </MenuItem>
                      <MenuItem className={classes.menuItem} value="Genderfluid">
                        Genderfluid
                      </MenuItem>
                      <MenuItem className={classes.menuItem} value="Other">
                        Other
                      </MenuItem>
                      <MenuItem
                        className={classes.menuItem}
                        value="Prefer not to say"
                      >
                        Prefer not to say
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {!isCreator && (
                <>
                  {/* annualIncome */}
                  {/* <Box className={classes.formRow}> */}
                  <Box className={classes.inputsBox}>
                    <Typography className={classes.label}>Annual Income</Typography>
                    <Box className={classes.fieldContainer}>
                      <FormControl
                        variant="outlined"
                        fullWidth
                        size="small"
                        className={classes.inputField}
                      >
                        <Select
                          name="annualIncome"
                          value={profile.annualIncome}
                          onChange={handleInputChange}
                          MenuProps={{
                            getContentAnchorEl: null,
                            anchorOrigin: { vertical: "bottom", horizontal: "left" },
                            transformOrigin: { vertical: "top", horizontal: "left" },
                            classes: { paper: classes.dropdownPaper },
                          }}
                          IconComponent={(props) => (
                            <KeyboardArrowDownIcon {...props} style={{ color: "#fff", fontSize: 20 }} />
                          )}
                        >
                          <MenuItem
                            className={classes.menuItem}
                            value="Less than $25,000"
                          >
                            Less than $25,000
                          </MenuItem>
                          <MenuItem
                            className={classes.menuItem}
                            value="$25,000 – $49,999"
                          >
                            $25,000 – $49,999
                          </MenuItem>
                          <MenuItem
                            className={classes.menuItem}
                            value="$50,000 – $74,999"
                          >
                            $50,000 – $74,999
                          </MenuItem>
                          <MenuItem
                            className={classes.menuItem}
                            value="$75,000 – $99,999"
                          >
                            $75,000 – $99,999
                          </MenuItem>
                          <MenuItem
                            className={classes.menuItem}
                            value="$100,000 – $149,999"
                          >
                            $100,000 – $149,999
                          </MenuItem>
                          <MenuItem
                            className={classes.menuItem}
                            value="$150,000 – $249,999"
                          >
                            $150,000 – $249,999
                          </MenuItem>
                          <MenuItem
                            className={classes.menuItem}
                            value="$250,000 – $499,999"
                          >
                            $250,000 – $499,999
                          </MenuItem>
                          <MenuItem className={classes.menuItem} value="$500,000+">
                            $500,000+
                          </MenuItem>
                          <MenuItem
                            className={classes.menuItem}
                            value="Prefer not to say"
                          >
                            Prefer not to say
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                  {/* </Box> */}
                </>
              )}

            </Box>
            <Box display="flex" justifyContent="space-between"
              className={classes.formButtonsWrapper}
            >
              <Button
                variant="outlined"
                className={clsx(classes.formButtons, classes.cancelButton)}
                onClick={getUserFields}
              >
                Cancel
              </Button>
              {/* Submit Button */}
              <Button
                variant="contained"
                className={clsx(classes.formButtons, classes.submitButton)}
                onClick={handleSave}
              >
                Save changes
              </Button>
            </Box>
          </Box>
        </Box>
      </>
    </Box >
  );
};

export default PersonalInformation;
